import React from 'react'
import { useForm } from '@mantine/form'
import { Button, Paper, Grid, TextInput, Radio, Group, Stack } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { useQuery } from '@tanstack/react-query'
import { useAppTranslation } from 'hooks/useTranslation'

import { SelectClearable } from 'ui/SelectClearable'
import { queryAdvertisers } from 'supabase/advertisers'
import { CreateCampaignData } from 'supabase/campaigns'
import { LANGUAGE_TARGET_OPTIONS, PLATFORM_TARGET_OPTIONS } from 'supabase/campaigns/targeting'

export interface CampaignFormProps {
  onSubmit: (data: CreateCampaignData) => void
  loading?: boolean
  initialValues?: Partial<CreateCampaignData>
}


export function CampaignForm({ onSubmit, loading = false, initialValues }: CampaignFormProps) {
  const { t } = useAppTranslation()

  const form = useForm<CreateCampaignData>({
    initialValues: {
      name: '',
      advertiser_id: '',
      tier: 'Standard' as const,
      language_target: 'all' as const,
      platform_target: 'all' as const,
      start_date: '',
      end_date: '',
      ...initialValues
    },
    validate: {
      name: (value) => {
        if (!value) return 'Campaign name is required'
        if (value.length < 2) return 'Campaign name should have at least 2 characters'
        return null
      },
      advertiser_id: (value) => (!value ? 'Advertiser is required' : null),
      tier: (value) => (!value ? 'Tier is required' : null),
      start_date: (value) => (!value ? 'Start date is required' : null),
      end_date: (value, values) => {
        if (!value) return 'End date is required'
        if (values.start_date && new Date(value) < new Date(values.start_date)) {
          return 'End date must be after start date'
        }
        return null
      }
    }
  })

  // Fetch advertisers for dropdown
  const { data: advertisersData, isLoading: advertisersLoading } = useQuery({
    queryKey: ['advertisers'],
    queryFn: () => queryAdvertisers()
  })

  const advertiserOptions = advertisersData?.advertisers.map(advertiser => ({
    value: advertiser.id,
    label: `${advertiser.name} (${advertiser.category})`
  })) || []

  const handleSubmit = (values: CreateCampaignData) => {
    onSubmit(values)
  }

  return (
    <Paper p="md">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Grid>
            <Grid.Col span={12}>
              <TextInput
                label={t('campaigns.name') as string}
                placeholder={t('campaigns.name_placeholder') as string}
                required
                {...form.getInputProps('name')}
              />
            </Grid.Col>

            <Grid.Col span={12}>
              <div>
                <label style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px', display: 'block' }}>
                  {t('campaigns.advertiser')} *
                </label>
                <SelectClearable
                  placeholder={t('campaigns.select_advertiser') as string}
                  data={advertiserOptions}
                  value={form.values.advertiser_id}
                  onChange={(value) => form.setFieldValue('advertiser_id', value || '')}
                  loading={advertisersLoading}
                  emptyMessage={t('campaigns.no_advertisers_found') as string}
                />
                {form.errors.advertiser_id && (
                  <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                    {form.errors.advertiser_id}
                  </div>
                )}
              </div>
            </Grid.Col>

            <Grid.Col span={6}>
              <div>
                <label style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px', display: 'block' }}>
                  {t('campaigns.tier')} *
                </label>
                <Radio.Group
                  {...form.getInputProps('tier')}
                >
                  <Stack gap="xs">
                    <Radio value="Premium" label={t('campaigns.tier_premium')} />
                    <Radio value="Standard" label={t('campaigns.tier_standard')} />
                    <Radio value="Starter" label={t('campaigns.tier_starter')} />
                  </Stack>
                </Radio.Group>
              </div>
            </Grid.Col>

            <Grid.Col span={6}>
              <div>
                <label style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px', display: 'block' }}>
                  {t('campaigns.language_target')} *
                </label>
                <Radio.Group
                  {...form.getInputProps('language_target')}
                >
                  <Stack gap="xs">
                    {LANGUAGE_TARGET_OPTIONS.map((option) => (
                      <Radio key={option.value} value={option.value} label={t(option.labelKey)} />
                    ))}
                  </Stack>
                </Radio.Group>
              </div>
            </Grid.Col>

            <Grid.Col span={6}>
              <div>
                <label style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px', display: 'block' }}>
                  {t('campaigns.platform_target')} *
                </label>
                <Radio.Group
                  {...form.getInputProps('platform_target')}
                >
                  <Stack gap="xs">
                    {PLATFORM_TARGET_OPTIONS.map((option) => (
                      <Radio key={option.value} value={option.value} label={t(option.labelKey)} />
                    ))}
                  </Stack>
                </Radio.Group>
              </div>
            </Grid.Col>

            <Grid.Col span={6}>
              <DatePickerInput
                label={t('campaigns.start_date') as string}
                placeholder={t('campaigns.start_date_placeholder') as string}
                required
                valueFormat="YYYY-MM-DD"
                value={form.values.start_date ? new Date(form.values.start_date) : null}
                onChange={(date) => {
                  form.setFieldValue('start_date', date ? date.toISOString().split('T')[0] : '')
                }}
                error={form.errors.start_date}
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <DatePickerInput
                label={t('campaigns.end_date') as string}
                placeholder={t('campaigns.end_date_placeholder') as string}
                required
                valueFormat="YYYY-MM-DD"
                value={form.values.end_date ? new Date(form.values.end_date) : null}
                onChange={(date) => {
                  form.setFieldValue('end_date', date ? date.toISOString().split('T')[0] : '')
                }}
                error={form.errors.end_date}
              />
            </Grid.Col>
          </Grid>

          <Group justify="flex-end">
            <Button
              type="submit"
              loading={loading}
              disabled={!form.isValid()}
            >
              {t('campaigns.create_campaign')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Paper>
  )
}